import React, { useEffect, useState, useRef } from 'react';

const BlurText = ({
  text,
  delay = 62,
  className = '',
  animateOnView = true,
  direction = 'top' // top, bottom, left, right
}) => {
  const [hasAnimated, setHasAnimated] = useState(!animateOnView);
  const [animatedWords, setAnimatedWords] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!animateOnView) {
      setHasAnimated(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    // Check if element is already past the viewport (user scrolled fast)
    const rect = el.getBoundingClientRect();
    if (rect.bottom < window.innerHeight) {
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [animateOnView]);

  useEffect(() => {
    if (!hasAnimated) return;

    const words = text.split(' ');

    // If element already scrolled past, show all instantly
    const el = ref.current;
    const skipAnimation = el && el.getBoundingClientRect().bottom < 0;

    if (skipAnimation) {
      setAnimatedWords(words.map(() => true));
      return;
    }

    setAnimatedWords(words.map(() => false));

    words.forEach((_, index) => {
      setTimeout(() => {
        setAnimatedWords(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * delay);
    });
  }, [hasAnimated, text, delay]);

  const words = text.split(' ');

  const getTransform = (isAnimated) => {
    if (isAnimated) return 'translate3d(0, 0, 0)';

    switch (direction) {
      case 'top':
        return 'translate3d(0, -20px, 0)';
      case 'bottom':
        return 'translate3d(0, 20px, 0)';
      case 'left':
        return 'translate3d(-20px, 0, 0)';
      case 'right':
        return 'translate3d(20px, 0, 0)';
      default:
        return 'translate3d(0, -20px, 0)';
    }
  };

  return (
    <span ref={ref} className={`blur-text-container ${className}`}>
      {words.map((word, index) => (
        <span
          key={index}
          className="blur-text-word"
          style={{
            display: 'inline-block',
            filter: animatedWords[index] ? 'blur(0px)' : 'blur(10px)',
            opacity: animatedWords[index] ? 1 : 0,
            transform: getTransform(animatedWords[index]),
            transition: `all 0.5s cubic-bezier(0.4, 0, 0.2, 1)`,
            marginRight: '0.25em',
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
};

export default BlurText;
